/**
 * Audio store for managing TTS playback state
 */

import { create } from 'zustand'

export interface WhisperWord {
  word: string
  start: number
  end: number
  break?: boolean
}

interface AudioStoreState {
  status: 'idle' | 'loading' | 'playing' | 'paused' | 'error' | 'resumed' | 'skipped'
  streamId: string | null
  cacheKey: string | null
  url: string | null
  whisperUrl: string | null
  whisperWords: WhisperWord[]
  whisperComplete: boolean
  isMuted: boolean
  /** Stream that already finished on this device; never cleared by setStopped. */
  lastFinishedStreamId: string | null

  setLoading: (streamId: string, url: string, cacheKey?: string, whisperUrl?: string | null) => void
  setPlaying: () => void
  setPaused: () => void
  setStopped: () => void
  setError: () => void
  setResumed: (streamId: string, cacheKey?: string) => void
  setSkipped: () => void
  setMuted: (muted: boolean) => void
  markFinished: (streamId: string) => void
  setWhisperUrl: (url: string | null) => void
  setWhisperData: (words: WhisperWord[], complete: boolean) => void
  clearWhisper: () => void
}

export const useAudioStore = create<AudioStoreState>((set) => ({
  status: 'idle',
  streamId: null,
  cacheKey: null,
  url: null,
  whisperUrl: null,
  whisperWords: [],
  whisperComplete: false,
  isMuted: false,
  lastFinishedStreamId: null,

  setLoading: (streamId, url, cacheKey, whisperUrl) => {
    console.log('[audioStore] setLoading called with:', { streamId, url, cacheKey, whisperUrl })
    set({
      status: 'loading',
      streamId,
      url,
      cacheKey: cacheKey !== undefined ? cacheKey : null,
      whisperUrl: whisperUrl === undefined ? null : whisperUrl,
      whisperWords: [],
      whisperComplete: false,
    })
  },
  setPlaying: () => set({ status: 'playing' }),
  setPaused: () => set({ status: 'paused' }),
  setStopped: () => set({ status: 'idle', streamId: null, cacheKey: null, url: null, whisperUrl: null, whisperWords: [], whisperComplete: false }),
  setError: () => set({ status: 'error' }),
  setResumed: (streamId, cacheKey) => set({
    status: 'resumed',
    streamId,
    cacheKey: cacheKey !== undefined ? cacheKey : null,
    whisperWords: [],
    whisperComplete: false,
    url: null,
  }),
  setSkipped: () =>
    set((state) => ({
      status: 'skipped',
      streamId: state.streamId,
      url: state.url,
      whisperWords: state.whisperWords,
      whisperComplete: state.whisperComplete,
    })),
  setMuted: (muted) => set({ isMuted: muted }),
  markFinished: (streamId) => set({ lastFinishedStreamId: streamId }),
  setWhisperUrl: (url) => set({ whisperUrl: url }),
  setWhisperData: (words, complete) => set({ whisperWords: words, whisperComplete: complete }),
  clearWhisper: () => set({ whisperUrl: null, whisperWords: [], whisperComplete: false }),
}))
